import type { ReactNode } from 'react';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { cn } from '@/lib/cn';
import { porcentaje } from '@/lib/formato-tienda';

/**
 * ENCABEZADO DE MÓDULO — el mismo en las cuatro pantallas.
 *
 * Título, una línea de qué es, la fila de KPIs y las acciones a la derecha.
 * Hoy cada pantalla lo arma distinto y por eso ninguna se parece a la otra.
 */
export function ModuleHeader({
  titulo,
  subtitulo,
  acciones,
  kpis,
  children,
}: {
  titulo: string;
  subtitulo?: string;
  acciones?: ReactNode;
  kpis?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{titulo}</h1>
          {subtitulo && <p className="mt-0.5 text-sm text-slate-500">{subtitulo}</p>}
        </div>
        {acciones && <div className="flex flex-wrap items-center gap-2">{acciones}</div>}
      </div>
      {children}
      {kpis && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{kpis}</div>}
    </header>
  );
}

export interface KpiProps {
  titulo: string;
  valor: string;
  /** Segunda línea: el detalle que explica el número. */
  pie?: ReactNode;
  /** Variación contra el período anterior (fracción). `null` = no había con qué comparar. */
  delta?: number | null;
  /** Cuando bajar es bueno (gastos, devoluciones), se invierte el color. */
  bajarEsBueno?: boolean;
  /** Clic: lleva a la tabla que explica el número. */
  onClick?: () => void;
  tono?: 'normal' | 'bueno' | 'malo';
}

/**
 * UN KPI — y **es clicable**.
 *
 * Maciel: cada número de Finanzas tiene que llevar a la tabla que lo explica.
 * Un tablero de puros números que no se pueden abrir obliga a creerle o a
 * ignorarlo; no hay tercera opción.
 */
export function Kpi({ titulo, valor, pie, delta, bajarEsBueno, onClick, tono = 'normal' }: KpiProps) {
  const subeEsBueno = !bajarEsBueno;
  const positivo = delta != null && delta > 0;
  const negativo = delta != null && delta < 0;
  const bueno = (positivo && subeEsBueno) || (negativo && !subeEsBueno);

  const Contenedor = onClick ? 'button' : 'div';

  return (
    <Contenedor
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'rounded-lg border border-surface-border bg-surface p-4 text-left',
        onClick &&
          'cursor-pointer transition-colors hover:border-brand-300 hover:bg-brand-50/40 dark:hover:bg-brand-900/10',
      )}
    >
      <p className="text-sm text-slate-500">{titulo}</p>
      <p
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums',
          tono === 'malo' && 'text-red-600 dark:text-red-400',
          tono === 'bueno' && 'text-green-700 dark:text-green-400',
          tono === 'normal' && 'text-slate-900 dark:text-slate-100',
        )}
      >
        {valor}
      </p>

      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
        {delta != null && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 font-medium',
              bueno ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400',
            )}
          >
            {positivo ? (
              <ArrowUp className="h-3 w-3" />
            ) : negativo ? (
              <ArrowDown className="h-3 w-3" />
            ) : (
              <Minus className="h-3 w-3" />
            )}
            {porcentaje(Math.abs(delta))}
          </span>
        )}
        {delta === null && <span className="text-slate-400">sin período anterior</span>}
        {pie && <span className="text-slate-500">{pie}</span>}
      </div>
    </Contenedor>
  );
}
